import './OneTimePassword.css';
import '../common-style.css';

import { useContext, useState, useEffect, useRef } from 'react';
import { loginWithOneTimePassword } from '../../connector/auth';
import { getAnonImportPath } from '../../connector/anon-migration';
import { FetchError } from 't-lib-ui/connector';
import { AppState } from '../../context/general';
import { noWhitespaceTrim, validateInput } from 't-lib-ui/helpers';
import { useProgressModal } from 't-lib-ui/structure';
import { RouterContext, redirect, getLocation } from 't-lib-ui/router';
import { FormGrid } from 't-lib-ui/structure';
import { Input, TextButton } from 't-lib-ui/widgets';
import { FormGridLine } from 't-lib-ui/structure';
import { Link } from 't-lib-ui/router';
import { FlowForm, useFlowFormGlobal } from 't-lib-ui/structure';
import { ErrorNote } from 't-lib-ui/widgets';
import DownreadLogo from '../../components/DownreadLogo';

function validatePasswordSimple(password: string): 'bad'|'missing'|undefined {
  if (!password.trim())
    return 'missing';
  return password.length >= 6 && password.length <= 100 ? undefined : 'bad';
}

const errorTexts = {
  bad: 'Please enter a valid code.',
  missing: 'You must enter the code.'
};

type StatusType = 'OK'|'Connection'|'LoginFailed'|'LoginTimeout'|'Waiting'|'NeedsConfirmation'|'Done';

const statusErrorMessages = {
  Connection: <>Failed to connect to server. Please try again later.</>,
  LoginFailed: <>Incorrect code. Please check your email and try again.</>,
  LoginTimeout: <>Too many failed attempts. Please wait.</>
};

const Page: React.FC = () => {
  const routerCtx = useContext(RouterContext);
  useFlowFormGlobal();

  const [presentLoading, dismissLoading] = useProgressModal();
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<'bad'|'missing'|undefined>(undefined);
  const [submitted, setSubmitted] = useState(false);
  const [status, setStatus] = useState<StatusType>('OK');
  // Overrides '/a' when the anon session has feeds to offer the freshly logged-in account.
  const [doneTarget, setDoneTarget] = useState<string|undefined>(undefined);
  const appState = useContext(AppState);
  const autoLoginAttempted = useRef(false);

  // Check for URL parameters
  const location = getLocation(routerCtx);
  const urlUserId = location.searchParams.get('userId');
  const urlConfirmationCode = location.searchParams.get('confirmationCode');

  // Get email from URL or session storage
  let email = sessionStorage.getItem('otp-email');
  if (urlUserId) {
    email = urlUserId;
    sessionStorage.setItem('otp-email', urlUserId);
  }

  // Auto-login if URL parameters are present
  useEffect(() => {
    if (urlUserId && urlConfirmationCode && !autoLoginAttempted.current) {
      autoLoginAttempted.current = true;
      setStatus('Waiting');
      presentLoading({
        message: 'Verifying code...',
        timeout: 5000,
      });

      loginWithOneTimePassword(urlUserId, urlConfirmationCode).then(({authData, user}) => {
        if (user && authData.selectedMembership) {
          appState.setLoggedIn(authData, user);
          setDoneTarget(getAnonImportPath(user, '/a'));
          setStatus('Done');
        }
        else if (authData.status === 'unconfirmed') {
          appState.setUnconfirmed(authData);
          setStatus('NeedsConfirmation');
        }
        else {
          throw new Error('No membership selected after login');
        }
        dismissLoading();
      }).catch((e) => {
        setStatus((e instanceof FetchError && ['Connection', 'LoginFailed', 'LoginTimeout'].includes(e.type)) ? e.type as any : 'Connection');
        dismissLoading();
      });
    }
  }, [urlUserId, urlConfirmationCode, appState, presentLoading, dismissLoading]);

  if (!email)
    return redirect(routerCtx, '/login');

  if (status === 'NeedsConfirmation')
    return redirect(routerCtx, "/confirm-email");
  if (status === 'Done')
    return redirect(routerCtx, doneTarget ?? '/a');

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    if (submitted) {
      validateInput(noWhitespaceTrim(value), validatePasswordSimple, setPasswordError);
    }
  };

  const loginClicked = (): boolean => {
    setSubmitted(true);
    setStatus('Waiting');

    const trimmedPassword = noWhitespaceTrim(password);

    if (!validateInput(trimmedPassword, validatePasswordSimple, setPasswordError)) {
      setStatus('OK');
      return true;
    }

    presentLoading({
      message: 'Verifying code...',
      timeout: 5000,
    });

    loginWithOneTimePassword(email, trimmedPassword).then(({authData, user}) => {
      if (user && authData.selectedMembership) {
        appState.setLoggedIn(authData, user);
        setDoneTarget(getAnonImportPath(user, '/a'));
        setStatus('Done');
      }
      else if (authData.status === 'unconfirmed') {
        appState.setUnconfirmed(authData);
        setStatus('NeedsConfirmation');
      }
      else {
        throw new Error('No membership selected after login');
      }
      dismissLoading();
    }).catch((e) => {
      setStatus((e instanceof FetchError && ['Connection', 'LoginFailed', 'LoginTimeout'].includes(e.type)) ? e.type as any : 'Connection');
      dismissLoading();
    });
    return true;
  };

  return (
    <FlowForm main={true}>
      <div className="authPage">
        <div className="authCard otpCard">
          <div className="authLogo">
            <a href="https://downread.com" className="authLogoLink"><DownreadLogo size={48} showText /></a>
          </div>

          <div className="otpInfo">
            We sent a code to <strong>{email}</strong>. Please enter it below.
          </div>

          <ErrorNote errorId={status} errorTexts={statusErrorMessages} />

          <FormGrid>
            <FormGridLine span={2} className='otpInputRow'>
              <Input type="text"
                label="Code"
                layout='singleCol'
                errorId={passwordError} errorTexts={errorTexts}
                placeholder='Enter 6-character code'
                autoComplete='one-time-code'
                enterKeyHint="enter"
                maxLength={10}
                value={password}
                className='password uppercase-input'
                onInput={handlePasswordChange}/>
            </FormGridLine>
            <FormGridLine span={2} align='center' className='otpSubmitRow'>
              <TextButton type='major' onClick={loginClicked} className='next' label='Verify Code'/>
            </FormGridLine>
          </FormGrid>

          <div className="authLink">
            Didn&apos;t receive a code? <Link to={'/login'}>Try again</Link>
          </div>
        </div>
      </div>
    </FlowForm>
  );
};

export default Page;
